import { mergeClasses } from "@fluentui/react-components";
import React, { useCallback, useEffect, useRef, useState } from "react";
import type { MapRef } from "react-map-gl/maplibre";
import { useMapSas } from "../../../hooks/useMapSas";
import { IFlightMarker } from "../../../models/airports/AirportConfig";
import { getAirportCode } from "../../../store/airport/AirportSelector";
import { useAppSelector } from "../../../store/StoreHooks";
import { getAirportConfig } from "../../../utils/common/CommonHelperFunctions";
import { mapApiUrl } from "../../../utils/maps/MapConstants";
import { getAzureMapTileUrl } from "../../../utils/maps/MapHelperFunctions";
import DisplayMapErrors from "../../osm-maps/DisplayMapErrors";
import MapLibreMap from "../core/MapLibreMap";
import { useMapFailures } from "../hooks/useMapFailures";
import AirportMarker from "../map-markers/AirportMarker";
import SelectedFlightMarker from "../map-markers/flight-markers/SelectedFlightMarker";
import { mapDisplayErrorStyles, mapLibreStyles } from "./mapStyles";

export interface SingleFlightMapProps {
  flightmarkerDetails: IFlightMarker;
  isLive: boolean;
  className?: string;
}

const SingleFlightMap: React.FC<SingleFlightMapProps> = ({
  flightmarkerDetails,
  isLive = false,
  className,
}) => {
  const styles = mapLibreStyles();
  const errorStyles = mapDisplayErrorStyles();
  const mapRef = useRef<MapRef>(null);
  const airport = useAppSelector((state) => getAirportCode(state));
  const airportMapDetail = getAirportConfig(airport);
  const [mapKey, setMapKey] = useState(0);
  const { mapApiFailures, handleMapApiFailure } = useMapFailures();
  const {
    sas: mapSas,
    error: mapSasError,
    forceRefresh: refreshMapSas,
    needsReset: mapSasNeedsReset,
    acknowledgeReset: acknowledgeMapSasReset,
  } = useMapSas(airport);
  const azureBaseMapUrl = getAzureMapTileUrl(
    airport,
    mapApiUrl,
    "BASE",
    mapSas
  );
  const isInvalidFlight =
    !isLive ||
    !Number.isFinite(Number(flightmarkerDetails.latitude)) ||
    !Number.isFinite(Number(flightmarkerDetails.longitude));
  const mapCenter: [number, number] = isInvalidFlight
    ? (airportMapDetail.location as [number, number])
    : [flightmarkerDetails.latitude, flightmarkerDetails.longitude];

  useEffect(() => {
    handleMapApiFailure("NO_FLIGHTS", !isInvalidFlight);
  }, [isInvalidFlight, handleMapApiFailure]);

  useEffect(() => {
    handleMapApiFailure("SAS", !mapSasError);
  }, [mapSasError, handleMapApiFailure]);

  useEffect(() => {
    if (mapSasNeedsReset) {
      setMapKey((value) => value + 1);
      acknowledgeMapSasReset();
    }
  }, [mapSasNeedsReset, acknowledgeMapSasReset]);

  const handleMarkerClick = useCallback(() => undefined, []);

  return (
    <section
      className={mergeClasses(
        "w-100 h-100",
        className,
        styles.embeddedMapContainer
      )}
      data-testid="maplibre-single-flight-map-container"
    >
      <MapLibreMap
        key={mapKey}
        mapRef={mapRef}
        center={mapCenter}
        baseMapUrl={mapSas ? azureBaseMapUrl : undefined}
        onMapApiFailure={handleMapApiFailure}
        onAuthError={refreshMapSas}
        testId="maplibre-single-flight-canvas"
      >
        <AirportMarker
          code={airportMapDetail.iataCode}
          name={airportMapDetail.name}
          position={airportMapDetail.location as [number, number]}
        />
        {!isInvalidFlight && (
          <SelectedFlightMarker
            selectedFlightMarkerDetails={flightmarkerDetails}
            airportCode={airportMapDetail.iataCode}
            handleMarkerClick={handleMarkerClick}
          />
        )}
        <DisplayMapErrors
          messages={mapApiFailures}
          className={errorStyles.singleFlightMap}
        />
      </MapLibreMap>
    </section>
  );
};

export default SingleFlightMap;
